<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  contactId: { type: Number, default: null },
  isInstagramContact: { type: Boolean, default: false },
  isMessengerContact: { type: Boolean, default: false },
});
const emit = defineEmits(['completed']);
const provider = computed(() =>
  props.isMessengerContact ? 'messenger' : 'instagram'
);

const { t: translate } = useI18n();
// Reutiliza a mesma confirmação localizada para os dois canais sociais.
const t = (...args) => {
  const text = translate(...args);
  return props.isMessengerContact
    ? text.replaceAll('Instagram', 'Messenger')
    : text;
};
const suggestions = ref([]);
const dismissed = ref(new Set());
const isLoading = ref(false);
const pendingId = ref('');
const confirmingId = ref('');
const errorMessage = ref('');
const successMessage = ref('');
let suggestionsRequestVersion = 0;
let confirmationRequestVersion = 0;
const visibleSuggestions = computed(() =>
  suggestions.value.filter(item => !dismissed.value.has(item.id))
);

const loadSuggestions = async () => {
  suggestionsRequestVersion += 1;
  const requestVersion = suggestionsRequestVersion;
  confirmationRequestVersion += 1;
  pendingId.value = '';
  confirmingId.value = '';
  suggestions.value = [];
  dismissed.value = new Set();
  errorMessage.value = '';
  successMessage.value = '';
  if (
    !(props.isInstagramContact || props.isMessengerContact) ||
    !props.contactId
  ) {
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  try {
    const { data } = await ContactAPI.instagramMergeSuggestions(
      props.contactId,
      provider.value
    );
    if (requestVersion !== suggestionsRequestVersion) return;
    suggestions.value = data?.data || [];
  } catch (error) {
    if (requestVersion === suggestionsRequestVersion) {
      errorMessage.value = t('INSTAGRAM_MERGE_SUGGESTIONS.ERROR');
    }
  } finally {
    if (requestVersion === suggestionsRequestVersion) {
      isLoading.value = false;
    }
  }
};

const dismissSuggestion = id => {
  errorMessage.value = '';
  if (confirmingId.value === id) confirmingId.value = '';
  dismissed.value = new Set([...dismissed.value, id]);
};

const confirmSuggestion = async suggestion => {
  const sourceContactId = props.contactId;
  confirmationRequestVersion += 1;
  const requestVersion = confirmationRequestVersion;
  pendingId.value = suggestion.id;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const { data } = await ContactAPI.confirmInstagramMerge(
      sourceContactId,
      suggestion.id,
      provider.value
    );

    // The server can finish after the operator has moved to another
    // conversation. Keep the old result out of the new contact's UI state.
    if (
      requestVersion !== confirmationRequestVersion ||
      sourceContactId !== props.contactId
    ) {
      return;
    }

    dismissSuggestion(suggestion.id);
    successMessage.value = data?.merged
      ? t('INSTAGRAM_MERGE_SUGGESTIONS.MERGED')
      : t('INSTAGRAM_MERGE_SUGGESTIONS.LINKED');
    emit('completed', data);
  } catch (error) {
    if (
      requestVersion === confirmationRequestVersion &&
      sourceContactId === props.contactId
    ) {
      errorMessage.value =
        error?.response?.data?.error || t('INSTAGRAM_MERGE_SUGGESTIONS.ERROR');
    }
  } finally {
    if (
      requestVersion === confirmationRequestVersion &&
      sourceContactId === props.contactId
    ) {
      pendingId.value = '';
    }
  }
};

const reasonLabel = reason => {
  if (reason === 'name') return t('INSTAGRAM_MERGE_SUGGESTIONS.REASONS.NAME');
  if (reason === 'email') return t('INSTAGRAM_MERGE_SUGGESTIONS.REASONS.EMAIL');
  if (reason === 'phone') return t('INSTAGRAM_MERGE_SUGGESTIONS.REASONS.PHONE');
  if (reason === 'instagram') {
    return t('INSTAGRAM_MERGE_SUGGESTIONS.REASONS.INSTAGRAM');
  }
  return '';
};
const conflictLabel = conflict =>
  conflict === 'weak_identity'
    ? t('INSTAGRAM_MERGE_SUGGESTIONS.WEAK_IDENTITY')
    : t('INSTAGRAM_MERGE_SUGGESTIONS.INSTAGRAM_CONFLICT');
const confirmationLabel = suggestion =>
  suggestion.contact_id
    ? t('INSTAGRAM_MERGE_SUGGESTIONS.CONFIRM_MERGE')
    : t('INSTAGRAM_MERGE_SUGGESTIONS.CONFIRM_LINK');
const confirmationPrompt = suggestion =>
  suggestion.contact_id
    ? t('INSTAGRAM_MERGE_SUGGESTIONS.MERGE_PROMPT', {
        name:
          suggestion.name || t('INSTAGRAM_MERGE_SUGGESTIONS.UNKNOWN_CLIENT'),
      })
    : t('INSTAGRAM_MERGE_SUGGESTIONS.LINK_PROMPT', {
        name:
          suggestion.name || t('INSTAGRAM_MERGE_SUGGESTIONS.UNKNOWN_CLIENT'),
      });

watch(
  () => [props.contactId, props.isInstagramContact, props.isMessengerContact],
  loadSuggestions,
  {
    immediate: true,
  }
);
</script>

<template>
  <section
    v-show="
      (isInstagramContact || isMessengerContact) &&
      !isLoading &&
      !successMessage &&
      (visibleSuggestions.length || errorMessage)
    "
    class="w-full rounded-xl border border-n-weak bg-n-solid-1 p-3 md:p-4"
    aria-live="polite"
  >
    <div class="flex items-start gap-3">
      <span class="i-lucide-user-round-search mt-0.5 text-base text-n-blue-9" />
      <div class="min-w-0 flex-1">
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">
          {{ t('INSTAGRAM_MERGE_SUGGESTIONS.TITLE') }}
        </h3>
        <p
          v-if="visibleSuggestions.length"
          class="mb-3 mt-1 text-sm leading-relaxed text-n-slate-11"
        >
          {{ t('INSTAGRAM_MERGE_SUGGESTIONS.DESCRIPTION') }}
        </p>

        <p v-if="errorMessage" class="mb-2 mt-0 text-sm text-n-ruby-9">
          {{ errorMessage }}
        </p>
        <p v-if="successMessage" class="mb-2 mt-0 text-sm text-n-teal-10">
          {{ successMessage }}
        </p>

        <ul
          v-if="visibleSuggestions.length"
          class="m-0 flex list-none flex-col gap-3 p-0"
        >
          <li
            v-for="suggestion in visibleSuggestions"
            :key="suggestion.id"
            class="rounded-lg border border-n-weak bg-n-background p-3"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="m-0 break-words text-sm font-medium text-n-slate-12">
                  {{
                    suggestion.name ||
                    suggestion.email ||
                    suggestion.phone ||
                    t('INSTAGRAM_MERGE_SUGGESTIONS.UNKNOWN_CLIENT')
                  }}
                </p>
                <div class="mt-2 flex flex-wrap gap-1">
                  <span
                    v-for="source in suggestion.sources"
                    :key="source"
                    class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                  >
                    {{ source }}
                  </span>
                </div>
                <p
                  v-if="suggestion.email"
                  class="mb-0 mt-1 break-all text-xs text-n-slate-11"
                >
                  {{ suggestion.email }}
                </p>
                <p
                  v-if="suggestion.phone"
                  class="mb-0 mt-1 break-all text-xs text-n-slate-11"
                >
                  {{ suggestion.phone }}
                </p>
                <p
                  v-if="suggestion.instagram"
                  class="mb-0 mt-1 break-all text-xs text-n-slate-11"
                >
                  {{ `@${suggestion.instagram}` }}
                </p>
                <p class="mb-0 mt-2 text-xs text-n-slate-11">
                  {{ t('INSTAGRAM_MERGE_SUGGESTIONS.MATCHED_BY') }}
                  {{ suggestion.match_reasons.map(reasonLabel).join(', ') }}
                </p>
                <p
                  v-if="suggestion.conflict"
                  class="mb-0 mt-2 text-xs text-n-ruby-9"
                >
                  {{ conflictLabel(suggestion.conflict) }}
                </p>
              </div>
            </div>

            <div
              v-if="confirmingId === suggestion.id"
              class="mt-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
            >
              <p class="m-0 text-sm leading-relaxed text-n-slate-12">
                {{ confirmationPrompt(suggestion) }}
              </p>
              <div class="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="instagram-merge-confirm"
                  class="inline-flex min-h-9 items-center justify-center rounded-lg border-0 bg-n-brand px-3 py-2 text-xs font-semibold text-white hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand disabled:opacity-50"
                  :disabled="Boolean(pendingId)"
                  @click="confirmSuggestion(suggestion)"
                >
                  {{
                    pendingId === suggestion.id
                      ? t('INSTAGRAM_MERGE_SUGGESTIONS.SAVING')
                      : t('INSTAGRAM_MERGE_SUGGESTIONS.CONFIRM_ACTION')
                  }}
                </button>
                <button
                  type="button"
                  class="inline-flex min-h-9 items-center justify-center rounded-lg border border-n-weak bg-n-background px-3 py-2 text-xs font-semibold text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
                  :disabled="Boolean(pendingId)"
                  @click="confirmingId = ''"
                >
                  {{ t('INSTAGRAM_MERGE_SUGGESTIONS.CANCEL') }}
                </button>
              </div>
            </div>
            <div v-else class="mt-3 flex flex-wrap gap-2">
              <button
                v-if="suggestion.can_merge"
                type="button"
                data-testid="instagram-merge-start"
                class="inline-flex min-h-9 items-center justify-center rounded-lg border-0 bg-n-brand px-3 py-2 text-xs font-semibold text-white hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand disabled:opacity-50"
                :disabled="Boolean(pendingId)"
                @click="confirmingId = suggestion.id"
              >
                {{ confirmationLabel(suggestion) }}
              </button>
              <button
                type="button"
                class="inline-flex min-h-9 items-center justify-center rounded-lg border border-n-weak bg-n-background px-3 py-2 text-xs font-semibold text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
                :disabled="Boolean(pendingId)"
                @click="dismissSuggestion(suggestion.id)"
              >
                {{ t('INSTAGRAM_MERGE_SUGGESTIONS.DISMISS') }}
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
